import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Plus,
  X,
  Calendar,
  CheckSquare,
  StickyNote,
  BarChart3,
  Clock,
  Layout,
  Grid,
  Target,
  Bell,
  FileText,
  Sparkles,
  ArrowRight,
  MoreVertical,
  TrendingUp,
  Activity,
  Zap,
  Brain,
  Lightbulb,
  Flame,
  Users,
  Heart,
  Coffee,
  Dumbbell,
  Briefcase,
  ChevronRight,
  Check,
  Star,
  Award,
  PieChart,
  LineChart,
  ChevronDown,
  SquareCheckBig,
  EllipsisVertical,
  User,
} from "lucide-react";
import { Filter } from "lucide-react";
import {
  LineChart as RechartsLine,
  Line,
  AreaChart,
  Area,
  BarChart as RechartsBar,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { FullDashboardDemo } from "./FullDashboardDemo";
import { InlineDashboard } from "./InlineDashboard";
import HeaderMenu from "../../Navbar/headermenu";
import CustomFooter from "../../Footer/customFooter";
import { Helmet } from "react-helmet";

export default function CustomDashboardFeature() {
  useEffect(() => {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 200);
  }, []);
  const [showWidgetPanel, setShowWidgetPanel] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState("work");
  const [activeWidgets, setActiveWidgets] = useState([
    "today",
    "tasks",
    "analytics",
    "calendar",
  ]);
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [aiInsightIndex, setAiInsightIndex] = useState(0);
  const [showFullDashboard, setShowFullDashboard] = useState(false);
  const [activeAIFeature, setActiveAIFeature] = useState(null);
  const [appliedSuggestions, setAppliedSuggestions] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const [activeFilters, setActiveFilters] = useState({
    project: "All Projects",
    financialYear: "2024-25",
    period: "This Month",
    assignee: "All Members",
  });
  const [isFiltered, setIsFiltered] = useState(false);
  const handleApplyFilters = () => {
    setIsFiltered(true);
    setShowFilterPanel(false);
  };

  const handleResetFilters = () => {
    setActiveFilters({
      project: "All Projects",
      financialYear: "2024-25",
      period: "This Month",
      assignee: "All Members",
    });
    setIsFiltered(false);
    setShowFilterPanel(false);
  };
  // ✅ NEW START: 4-tab animated preview (replaces static tabs preview)
  const [activePreviewTab, setActivePreviewTab] = useState("work");

  const PREVIEW_TABS = [
    {
      id: "work",
      label: "Work Projects",
      icon: Briefcase,
      active:
        "bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-blue-500/30",
      hover: "hover:border-blue-500",
    },
    {
      id: "clients",
      label: "Clients Management",
      icon: Users,
      active:
        "bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-purple-500/30",
      hover: "hover:border-purple-500",
    },
    {
      id: "sales",
      label: "Sales Pipeline",
      icon: Target,
      active:
        "bg-gradient-to-br from-green-500 to-green-600 text-white shadow-green-500/30",
      hover: "hover:border-green-500",
    },
    {
      id: "team",
      label: "Team Analytics",
      icon: BarChart3,
      active:
        "bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-orange-500/30",
      hover: "hover:border-orange-500",
    },
  ];

  const panelVariants = {
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
  };

  const gridVariants = {
    initial: {},
    animate: { transition: { staggerChildren: 0.08 } },
  };

  const cardVariants = {
    initial: { opacity: 0, y: 14, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
  };

  const renderPreviewCards = () => {
    // WORK
    if (activePreviewTab === "work") {
      return (
        <motion.div
          variants={gridVariants}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <CheckSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Active Tasks</p>
                <p className="text-2xl font-bold text-blue-600">24</p>
              </div>
            </div>

            <div className="h-20 bg-white/50 rounded-lg flex items-end gap-1 p-2 overflow-hidden">
              {[40, 60, 45, 75, 55, 85].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{
                    duration: 0.7,
                    delay: i * 0.06,
                    ease: "easeOut",
                  }}
                  className="flex-1 bg-blue-500 rounded-sm"
                />
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Events</p>
                <p className="text-2xl font-bold text-purple-600">8</p>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 14 }).map((_, i) => {
                const isMain = i === 6;
                const isSoft = i === 9 || i === 11;
                return (
                  <motion.div
                    key={i}
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.35, delay: 0.05 + i * 0.02 }}
                    className={`aspect-square rounded ${isMain ? "bg-purple-500" : isSoft ? "bg-purple-300" : "bg-white/50"}`}
                  />
                );
              })}
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Completed</p>
                <p className="text-2xl font-bold text-green-600">156</p>
              </div>
            </div>

            <div className="space-y-3">
              {[85, 92, 78].map((w, i) => (
                <div
                  key={i}
                  className="h-2 bg-white/50 rounded-full overflow-hidden"
                >
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${w}%` }}
                    transition={{
                      duration: 0.8,
                      delay: i * 0.12,
                      ease: "easeOut",
                    }}
                    className="h-full bg-green-500 rounded-full"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      );
    }

    // CLIENTS
    if (activePreviewTab === "clients") {
      return (
        <motion.div
          variants={gridVariants}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Total Clients</p>
                <p className="text-2xl font-bold text-purple-600">42</p>
              </div>
            </div>

            <div className="space-y-2">
              {["ZIAAT Corp", "Acme Inc", "TechStart"].map((name, i) => (
                <motion.div
                  key={name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.08 }}
                  className="flex items-center gap-2 bg-white/50 rounded-lg p-2"
                >
                  <div className="w-6 h-6 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full" />
                  <span className="text-xs text-gray-700">{name}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-6 border border-pink-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-pink-500 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Active Projects</p>
                <p className="text-2xl font-bold text-pink-600">12</p>
              </div>
            </div>

            <div className="space-y-2">
              {[
                { label: "Website Redesign", pct: 75 },
                { label: "Mobile App", pct: 45 },
                { label: "SEO Campaign", pct: 90 },
              ].map((p, i) => (
                <div key={p.label} className="space-y-1">
                  <p className="text-xs text-gray-700">{p.label}</p>
                  <div className="h-1.5 bg-white/50 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${p.pct}%` }}
                      transition={{
                        duration: 0.8,
                        delay: i * 0.12,
                        ease: "easeOut",
                      }}
                      className="h-full bg-pink-500 rounded-full"
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Documents</p>
                <p className="text-2xl font-bold text-blue-600">234</p>
              </div>
            </div>

            <div className="h-20 bg-white/50 rounded-lg p-3 space-y-2">
              {[60, 100, 40, 80].map((w, i) => (
                <motion.div
                  key={i}
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: `${w}%`, opacity: 1 }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className="h-2 bg-blue-400 rounded-full"
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      );
    }

    // SALES
    if (activePreviewTab === "sales") {
      return (
        <motion.div
          variants={gridVariants}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 border border-emerald-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Revenue</p>
                <p className="text-2xl font-bold text-emerald-600">$142K</p>
              </div>
            </div>

            <div className="h-20 bg-white/50 rounded-lg flex items-end gap-2 p-3 overflow-hidden">
              {[40, 55, 45, 70, 60, 78].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{
                    duration: 0.75,
                    delay: i * 0.07,
                    ease: "easeOut",
                  }}
                  className="flex-1 bg-gradient-to-b from-emerald-400 to-emerald-600 rounded-md"
                />
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-6 border border-emerald-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Deals Closed</p>
                <p className="text-2xl font-bold text-emerald-600">28</p>
              </div>
            </div>

            <div className="space-y-2">
              {[
                { label: "Hot Leads", value: 12 },
                { label: "Warm Leads", value: 24 },
                { label: "Cold Leads", value: 8 },
              ].map((row, i) => (
                <motion.div
                  key={row.label}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.08 }}
                  className="flex items-center justify-between bg-white/50 rounded-lg px-3 py-2"
                >
                  <p className="text-xs text-gray-700">{row.label}</p>
                  <p className="text-xs text-emerald-700">{row.value}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 border border-teal-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Win Rate</p>
                <p className="text-2xl font-bold text-teal-600">87%</p>
              </div>
            </div>
            <div className="relative h-20 flex items-center justify-center">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="#d1fae5"
                  strokeWidth="8"
                  fill="none"
                />
                <motion.circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="#14b8a6"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={226}
                  initial={{ strokeDashoffset: 226 }}
                  animate={{ strokeDashoffset: 226 - (226 * 87) / 100 }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </svg>
              <span className="absolute text-sm text-teal-600">87%</span>
            </div>
          </motion.div>
        </motion.div>
      );
    }

    // TEAM
    return (
      <motion.div
        variants={gridVariants}
        initial="initial"
        animate="animate"
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <motion.div
          variants={cardVariants}
          className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 border border-orange-200"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-900">Team Members</p>
              <p className="text-2xl font-bold text-orange-600">18</p>
              <div className="flex -space-x-2 mb-3">
                {[1, 2, 3, 4, 5].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-400 border-2 border-white"
                  />
                ))}
              </div>
              <p className="text-xs text-gray-600">Active this week</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={cardVariants}
          className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-6 border border-yellow-200"
        >
          {/* <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                            <Activity className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-900">Productivity</p>
                            <p className="text-2xl font-bold text-yellow-700">94%</p>
                            <div className="space-y-2">
                                {[95, 88, 92].map((score, i) => (
                                    <div key={i} className="space-y-1">
                                        <div className="h-2 bg-white/50 rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${score}%` }}
                                                transition={{ delay: i * 0.1, duration: 0.6 }}
                                                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div> */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-6 border border-amber-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Productivity</p>
                <p className="text-2xl text-amber-600">94%</p>
              </div>
            </div>
            <div className="space-y-2">
              {[95, 88, 92].map((score, i) => (
                <div key={i} className="space-y-1">
                  <div className="h-2 bg-white/50 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${score}%` }}
                      transition={{ delay: i * 0.1, duration: 0.6 }}
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={cardVariants}
          className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-2xl p-6 border border-rose-200"
        >
          {/* <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-rose-500 rounded-lg flex items-center justify-center">
                            <BarChart3 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-900">Tasks/Week</p>
                            <p className="text-2xl font-bold text-rose-600">127</p>
                        </div>
                    </div>
                    <div className="h-20 bg-white/50 rounded-lg" /> */}
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-900">Tasks/Week</p>
                <p className="text-2xl text-red-600">127</p>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {[65, 78, 82, 90, 75, 45, 30].map((height, i) => (
                <motion.div
                  key={i}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-gradient-to-t from-red-500 to-red-300 rounded-sm origin-bottom"
                  style={{ height: `${(height / 90) * 64}px` }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };
  // ✅ NEW END

  // ✅ NEW START: Tailwind-safe color class maps (fixes bg-${color}-500, from-${color}-500 issues)
  const COLOR = {
    blue: {
      bg: "bg-blue-500",
      bgLight: "bg-blue-50",
      text: "text-blue-600",
      border: "border-blue-200",
      from: "from-blue-500",
      to400: "to-blue-400",
      to600: "to-blue-600",
      shadow30: "shadow-blue-500/30",
    },
    purple: {
      bg: "bg-purple-500",
      bgLight: "bg-purple-50",
      text: "text-purple-600",
      border: "border-purple-200",
      from: "from-purple-500",
      to400: "to-purple-400",
      to600: "to-purple-600",
      shadow30: "shadow-purple-500/30",
    },
    green: {
      bg: "bg-green-500",
      bgLight: "bg-green-50",
      text: "text-green-600",
      border: "border-green-200",
      from: "from-green-500",
      to400: "to-green-400",
      to600: "to-green-600",
      shadow30: "shadow-green-500/30",
    },
    orange: {
      bg: "bg-orange-500",
      bgLight: "bg-orange-50",
      text: "text-orange-600",
      border: "border-orange-200",
      from: "from-orange-500",
      to400: "to-orange-400",
      to600: "to-orange-600",
      shadow30: "shadow-orange-500/30",
    },
    pink: {
      bg: "bg-pink-500",
      from: "from-pink-500",
      to400: "to-pink-400",
      to600: "to-pink-600",
      shadow30: "shadow-pink-500/30",
    },
    yellow: {
      bg: "bg-yellow-500",
      from: "from-yellow-500",
      to600: "to-yellow-600",
      shadow30: "shadow-yellow-500/30",
    },
    indigo: {
      bg: "bg-indigo-500",
      from: "from-indigo-500",
      to600: "to-indigo-600",
      shadow30: "shadow-indigo-500/30",
    },
    rose: {
      bg: "bg-rose-500",
      from: "from-rose-500",
      to400: "to-rose-400",
      to600: "to-rose-600",
      shadow30: "shadow-rose-500/30",
    },
  };
  // ✅ NEW END
  const COLORS = {
    blue: {
      from: "from-blue-500",
      to600: "to-blue-600",
      shadow30: "shadow-blue-500/30",
    },
    purple: {
      from: "from-purple-500",
      to600: "to-purple-600",
      shadow30: "shadow-purple-500/30",
    },
    green: {
      from: "from-green-500",
      to600: "to-green-600",
      shadow30: "shadow-green-500/30",
    },
  };
  // AI Insights that cycle
  const aiInsights = [
    {
      icon: Brain,
      title: "Peak Productivity Detected",
      description:
        "You complete 40% more tasks between 9-11 AM. Consider scheduling important work then.",
      color: "from-purple-500 to-blue-500",
      metric: "+40%",
    },
    {
      icon: Lightbulb,
      title: "Widget Suggestion",
      description:
        'Based on your calendar usage, adding the "Upcoming Events" widget could help you stay ahead.',
      color: "from-yellow-500 to-orange-500",
      metric: "Smart",
    },
    {
      icon: TrendingUp,
      title: "Efficiency Trend",
      description:
        "Your task completion rate increased 23% this week. Keep up the momentum!",
      color: "from-green-500 to-emerald-500",
      metric: "+23%",
    },
    {
      icon: Flame,
      title: "7-Day Streak",
      description:
        "You've checked your dashboard every day this week. Consistency breeds success!",
      color: "from-red-500 to-pink-500",
      metric: "7 Days",
    },
  ];

  // Interactive AI Suggestions
  const aiSuggestions = [
    {
      id: 0,
      icon: Lightbulb,
      title: "Add Focus Time Widget",
      description: "You work best 9-11 AM. Block this time automatically.",
      impact: "+40% productivity",
      color: "from-yellow-500 to-orange-500",
      preview: "focusTime",
    },
    {
      id: 1,
      icon: TrendingUp,
      title: "Enable Smart Prioritization",
      description:
        "AI will reorder tasks based on deadlines and energy levels.",
      impact: "3hrs saved/week",
      color: "from-blue-500 to-cyan-500",
      preview: "smartPriority",
    },
    {
      id: 2,
      icon: Zap,
      title: "Auto-Schedule Breaks",
      description:
        "Schedule 5-min breaks every 90 minutes for peak performance.",
      impact: "+25% focus",
      color: "from-green-500 to-emerald-500",
      preview: "autoBreaks",
    },
    {
      id: 3,
      icon: Target,
      title: "Goal Insights Dashboard",
      description: "Track progress with AI-powered forecasting and trends.",
      impact: "2x completion",
      color: "from-purple-500 to-pink-500",
      preview: "goalInsights",
    },
  ];

  // Cycle AI insights
  useEffect(() => {
    const interval = setInterval(() => {
      setAiInsightIndex((prev) => (prev + 1) % aiInsights.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Chart data
  const productivityData = [
    { day: "Mon", tasks: 12, hours: 6 },
    { day: "Tue", tasks: 19, hours: 8 },
    { day: "Wed", tasks: 15, hours: 7 },
    { day: "Thu", tasks: 22, hours: 9 },
    { day: "Fri", tasks: 18, hours: 7 },
    { day: "Sat", tasks: 8, hours: 3 },
    { day: "Sun", tasks: 5, hours: 2 },
  ];

  const goalData = [
    { name: "Completed", value: 68, color: "#10b981" },
    { name: "In Progress", value: 22, color: "#3b82f6" },
    { name: "Pending", value: 10, color: "#94a3b8" },
  ];

  // Available Widgets with enhanced data
  const availableWidgets = [
    {
      id: "today",
      name: "Today View",
      icon: Calendar,
      color: "from-blue-500 to-blue-600",
      description: "Your day at a glance",
      category: "Essentials",
    },
    {
      id: "tasks",
      name: "Active Tasks",
      icon: CheckSquare,
      color: "from-purple-500 to-purple-600",
      description: "Quick task overview",
      category: "Essentials",
    },
    {
      id: "analytics",
      name: "Productivity Analytics",
      icon: BarChart3,
      color: "from-emerald-500 to-emerald-600",
      description: "Track your performance",
      category: "Analytics",
    },
    {
      id: "calendar",
      name: "Calendar",
      icon: Calendar,
      color: "from-green-500 to-green-600",
      description: "Upcoming events",
      category: "Essentials",
    },
    {
      id: "notes",
      name: "Quick Notes",
      icon: StickyNote,
      color: "from-yellow-500 to-yellow-600",
      description: "Capture ideas instantly",
      category: "Tools",
    },
    {
      id: "goals",
      name: "Goals Progress",
      icon: Target,
      color: "from-red-500 to-red-600",
      description: "Track objectives",
      category: "Analytics",
    },
    {
      id: "activity",
      name: "Recent Activity",
      icon: Clock,
      color: "from-pink-500 to-pink-600",
      description: "Latest updates",
      category: "Tools",
    },
    // {
    //   id: "stats",
    //   name: "Weekly Stats",
    //   icon: TrendingUp,
    //   color: "from-cyan-500 to-cyan-600",
    //   description: "Performance metrics",
    //   category: "Analytics",
    // },
    {
      id: "team",
      name: "Team Activity",
      icon: Users,
      color: "from-indigo-500 to-indigo-600",
      description: "Collaborate better",
      category: "Tools",
    },
    // {
    //   id: "habits",
    //   name: "Habit Tracker",
    //   icon: Heart,
    //   color: "from-rose-500 to-rose-600",
    //   description: "Build consistency",
    //   category: "Personal",
    // },
  ];

  const dashboards = [
    {
      id: "work",
      name: "Work",
      icon: Briefcase,
      gradient: "from-purple-600 to-blue-600",
    },
    {
      id: "personal",
      name: "Personal",
      icon: Target,
      gradient: "from-blue-600 to-cyan-600",
    },
    {
      id: "fitness",
      name: "Fitness",
      icon: Dumbbell,
      gradient: "from-green-600 to-emerald-600",
    },
  ];

  // Dashboard templates
  const templates = [
    {
      name: "Productivity Pro",
      description: "Perfect for focused work",
      gradient: "from-purple-500 to-blue-500",
      widgets: ["Today View", "Active Tasks", "Analytics", "Calendar"],
      icon: Briefcase,
    },
    {
      name: "Life Balance",
      description: "Track work and wellness",
      gradient: "from-blue-500 to-teal-500",
      widgets: ["Goals", "Habits", "Notes", "Activity"],
      icon: Heart,
    },
    {
      name: "Team Leader",
      description: "Manage projects and people",
      gradient: "from-indigo-500 to-purple-500",
      widgets: ["Team Activity", "Tasks", "Calendar", "Stats"],
      icon: Users,
    },
    {
      name: "Creative Flow",
      description: "Organize ideas and inspiration",
      gradient: "from-pink-500 to-orange-500",
      widgets: ["Notes", "Today View", "Activity", "Goals"],
      icon: Sparkles,
    },
  ];

  const handleAddWidget = (widgetId) => {
    if (!activeWidgets.includes(widgetId)) {
      setActiveWidgets([...activeWidgets, widgetId]);
    }
  };

  const handleRemoveWidget = (widgetId) => {
    setActiveWidgets(activeWidgets.filter((id) => id !== widgetId));
  };

  // Render widget content based on type
  const renderWidgetContent = (widgetId) => {
    switch (widgetId) {
      case "today":
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              <div className="flex-1">
                <p className="text-sm text-gray-900">Team standup</p>
                <p className="text-xs text-gray-500">9:00 AM</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-purple-50 rounded-lg">
              <div className="w-2 h-2 bg-purple-500 rounded-full" />
              <div className="flex-1">
                <p className="text-sm text-gray-900">Design review</p>
                <p className="text-xs text-gray-500">2:00 PM</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 bg-green-50 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <div className="flex-1">
                <p className="text-sm text-gray-900">Project deadline</p>
                <p className="text-xs text-gray-500">5:00 PM</p>
              </div>
            </div>
          </div>
        );
      case "tasks":
        return (
          <div className="space-y-2">
            {[
              { task: "Complete Q4 report", progress: 75 },
              { task: "Review pull requests", progress: 100 },
              { task: "Update documentation", progress: 45 },
            ].map((item, i) => (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">{item.task}</p>
                  <span className="text-xs text-gray-500">
                    {item.progress}%
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.progress}%` }}
                    transition={{ duration: 1, delay: i * 0.2 }}
                    className={`h-full ${
                      item.progress === 100 ? "bg-green-500" : "bg-purple-500"
                    }`}
                  />
                </div>
              </div>
            ))}
          </div>
        );
      case "analytics":
        return (
          <div className="h-32 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={productivityData}>
                <defs>
                  <linearGradient id="colorTasks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="tasks"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#colorTasks)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );
      case "calendar":
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-7 gap-1 text-center">
              {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
                <div key={i} className="text-xs text-gray-400">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 21 }).map((_, i) => (
                <div
                  key={i}
                  className={`aspect-square flex items-center justify-center text-xs rounded ${
                    i === 10
                      ? "bg-blue-500 text-white"
                      : i === 15
                        ? "bg-purple-100 text-purple-700"
                        : "text-gray-700"
                  }`}
                >
                  {i + 1}
                </div>
              ))}
            </div>
          </div>
        );
      case "goals":
        return (
          <div className="space-y-3">
            {[
              { name: "Revenue Target", progress: 85, color: "green" },
              { name: "Client Satisfaction", progress: 92, color: "blue" },
              { name: "Team Growth", progress: 68, color: "purple" },
            ].map((goal, i) => {
              const barClass = COLOR?.[goal.color]?.bg || "bg-gray-400";
              return (
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-700">{goal.name}</span>
                    <span className="text-xs text-gray-900">
                      {goal.progress}%
                    </span>
                  </div>

                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${goal.progress}%` }}
                      transition={{ duration: 1, delay: i * 0.2 }}
                      className={`h-full ${barClass}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        );
      default:
        return (
          <div className="space-y-2">
            <div className="h-2 bg-gray-100 rounded-full w-full" />
            <div className="h-2 bg-gray-100 rounded-full w-4/5" />
            <div className="h-2 bg-gray-100 rounded-full w-3/5" />
          </div>
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>Your Workflow, Visualized | Simplifyte</title>
      </Helmet>
      <HeaderMenu />
      <div className="min-h-screen bg-black">
        {/* Hero Section - Dark with Spotlight Effect */}
        <section className="DashboardHero">
          {/* relative min-h-screen bg-black py-32 px-6 overflow-hidden flex items-center */}
          {/* Gradient Orbs */}
          {/* <div className="Hero_Orb Hero_Orb--purple" /> */}
          {/* absolute top-0 left-1/4 w-[600px] h-[600px] bg-purple-600/30 rounded-full blur-[120px] */}
          {/* <div className="Hero_Orb Hero_Orb--blue" /> */}
          {/* absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-blue-600/30 rounded-full blur-[120px] */}
          {/* Grid Pattern */}
          <div className="Hero_GridPattern" />
          {/* absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px] */}

          <div className="Hero_Container">
            {/* max-w-7xl mx-auto relative z-10 w-full */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="Hero_Header"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="Hero_Badge"
                // inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 text-purple-300 px-5 py-2 rounded-full mb-12 backdrop-blur-sm
              >
                <Sparkles className="Hero_BadgeIcon" />
                <span className="Hero_BadgeText">Custom Dashboards</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="Hero_Title"
                // text-7xl md:text-8xl lg:text-9xl text-white mb-8
              >
                Your Workflow,
                <br />
                <span className="Hero_TitleGradient">
                  {/* bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent */}
                  Visualized
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="Hero_Subtitle"
                // text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed mb-8
              >
                Create unlimited dashboards with AI-powered insights and
                customizable widgets.
                <br />
                Effortlessly visualize everything that matters.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="Hero_Stats"
                // flex items-center justify-center gap-16 text-center mt-20
              >
                <div className="Hero_Stat">
                  <p className="Hero_StatValue purple">
                    ∞
                  </p>
                  <p className="Hero_StatLabel">Dashboards</p>
                </div>
                <div className="Hero_StatDivider" />
                <div className="Hero_Stat">
                  <p className="Hero_StatValue blue">
                    50+
                  </p>
                  <p className="Hero_StatLabel">Widgets</p>
                </div>
                <div className="Hero_StatDivider" />
                {/* w-px h-20 bg-gray-800 */}
                <div className="Hero_Stat">
                  <p className="Hero_StatValue green">
                    {/* text-6xl bg-gradient-to-r from-cyan-400 to-green-400 bg-clip-text text-transparent mb-2 */}
                    AI
                  </p>
                  <p className="Hero_StatLabel">Powered</p>
                  {/* text-gray-500 */}
                </div>
              </motion.div>
            </motion.div>

            {/* Floating Dashboard Preview */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 1, duration: 1 }}
              className="Hero_PreviewWrapper"
              // relative group
            >
              {/* Glow Effect */}
              <div className="Hero_Glow" />
              {/* absolute inset-0 bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-cyan-600/20 blur-3xl */}

              {/* Spotlight Effect that follows cursor */}
              <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10" />
              </div>

              {/* Seamless Scroll Hint - Replaces Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.6, duration: 1 }}
                className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2"
              >
                {/* <p className="text-gray-600 text-sm">Explore more below</p> */}
                <motion.div
                  animate={{
                    y: [0, 8, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                </motion.div>
              </motion.div>
            </motion.div>
          </div>

          {/* Seamless Transition Gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-white pointer-events-none" />
        </section>

        {/* Dashboard Demo Section - was previously a modal, now inline */}
        {/* <section className="py-8 sm:py-16 md:py-32 px-6 bg-white">
          <div className="max-w-[1600px] mx-auto">
            <div className="text-center mb-10 sm:mb-16 md:mb-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="Dashboard_AC">See Your Dashboard In Action</h2>
                <p className="Dashboard_Exp text-gray-600 max-w-2xl mx-auto">
                  Experience the full power of custom dashboards with real data
                  and interactive widgets.
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <InlineDashboard />
            </motion.div>
          </div>
        </section> */}
        <section className="hero-section-dashboard">
          {/* Animated Background Blobs */}
          <div className="hero-background-blobs">
            <div className="blob blob-purple" />
            <div className="blob blob-blue" />
            <div className="blob blob-pink" />
          </div>

          <div className="hero-container-dashboard">
            {/* Hero Heading */}
            <div className="hero-heading-dashboard">
              <div className="hero-badge-dashboard">
                <svg
                  className="badge-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="M3 9h18" />
                  <path d="M9 21V9" />
                </svg>
                <span className="badge-text">Unlimited Flexibility</span>
              </div>

              <h2 className="hero-title-dashboard">
                Create{" "}
                <span className="title-gradient">Multiple Dashboards</span>
                <br />
                As Per Your Choice
              </h2>

              <p className="hero-subtitle">
                Organize your workflow with unlimited custom dashboards. Whether
                it's for different projects, teams, or client portfolios —
                create separate spaces tailored to your specific needs.
              </p>
            </div>

            {/* Dashboard Preview */}
            <div className="dashboard-preview-section">
              <div className="dashboard-preview">
                <div className="preview-tabs">
                  {PREVIEW_TABS.map((t) => {
                    const Icon = t.icon;
                    const isActive = activePreviewTab === t.id;
                    const activeGradient = (() => {
                      switch (t.id) {
                        case "work-projects":
                          return "bg-gradient-to-r from-blue-700 to-blue-400"; // Blue
                        case "clients-management":
                          return "bg-gradient-to-r from-purple-700 to-purple-400"; // Purple
                        case "sales-pipeline":
                          return "bg-gradient-to-r from-green-700 to-green-400"; // Green
                        case "team-analytics":
                          return "bg-gradient-to-r from-orange-600 to-orange-400"; // Orange
                        default:
                          return "bg-gradient-to-r from-blue-700 to-blue-400";
                      }
                    })();
                    return (
                      <motion.button
                        key={t.id}
                        type="button"
                        onClick={() => setActivePreviewTab(t.id)}
                        whileTap={{ scale: 0.98 }}
                        whileHover={{ y: -2 }}
                        // className={`preview-tab ${isActive ? `preview-tab-active ${activeBg}
                        //  text-white shadow-lg` : 'preview-tab-inactive bg-gray-100 text-gray-700'}transition-all duration-300`}
                        className={`preview-tab 
                                                                flex items-center gap-3 px-6 py-3 rounded-xl 
                                                                shadow-md transition-all duration-300
                                                                ${
                                                                  isActive
                                                                    ? `${activeGradient} text-white shadow-lg border-2 border-transparent`
                                                                    : "bg-white text-gray-700 border-2 border-gray-200 hover:bg-gray-50"
                                                                }
                                                                `}
                      >
                        <Icon className="tab-icon" />
                        <span className="tab-label">{t.label}</span>
                      </motion.button>
                    );
                  })}

                  <button type="button" className="add-tab-btn">
                    <Plus className="add-icon" />
                  </button>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activePreviewTab}
                    variants={panelVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  >
                    {renderPreviewCards()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Feature Cards */}
            <div className="features-grid-dashboard">
              {[
                {
                  icon: "panels-top-left",
                  color: "blue",
                  title: "Unlimited Dashboards",
                  desc: "Create as many dashboards as you need. No limits., no restrictions. Organize by project, team, client, or any way that suits your workflow.",
                },
                {
                  icon: "zap",
                  color: "purple",
                  title: "Quick Switching",
                  desc: "Seamlessly switch between dashboards with a single click. Access all your workspaces instantly without losing context or momentum.",
                },
                {
                  icon: "star",
                  color: "green",
                  title: "Fully Customizable",
                  desc: "Each dashboard is independently customizable. Add different widgets, set unique layouts, and personalize every detail to match your needs.",
                },
              ].map((item, i) => (
                <div key={i} className="feature-card-dashboard">
                  {/* Feature Icon */}
                  <div className={`feature-icon feature-icon-${item.color}`}>
                    <svg
                      className="feature-svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {item.icon === "panels-top-left" && (
                        <>
                          <rect width="18" height="18" x="3" y="3" rx="2" />
                          <path d="M3 9h18" />
                          <path d="M9 21V9" />
                        </>
                      )}
                      {item.icon === "zap" && (
                        <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
                      )}
                      {item.icon === "star" && (
                        <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                      )}
                    </svg>
                  </div>

                  <h3 className="feature-title-title">{item.title}</h3>
                  <p className="feature-description">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Use Cases Grid */}
            <div className="use-cases-section">
              <h3 className="use-cases-title">Perfect For Every Workflow</h3>
              <div className="use-cases-grid">
                {[
                  { icon: "briefcase", label: "Work Projects" },
                  { icon: "users", label: "Team Collaboration" },
                  { icon: "award", label: "Client Management" },
                  { icon: "chart-column", label: "Sales & Pipeline" },
                  { icon: "coffee", label: "Side Hustles" },
                  { icon: "file-text", label: "Project Tracking" },
                  { icon: "target", label: "Goal Tracking" },
                  { icon: "brain", label: "Learning & Dev" },
                ].map((item, i) => (
                  <div key={i} className="use-case-card">
                    <svg
                      className="use-case-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {item.icon === "briefcase" && (
                        <>
                          <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                          <rect width="20" height="14" x="2" y="6" rx="2" />
                        </>
                      )}
                      {item.icon === "users" && (
                        <>
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                        </>
                      )}
                      {item.icon === "award" && (
                        <>
                          <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526" />
                          <circle cx="12" cy="8" r="6" />
                        </>
                      )}
                      {item.icon === "chart-column" && (
                        <>
                          <path d="M3 3v16a2 2 0 0 0 2 2h16" />
                          <path d="M18 17V9" />
                          <path d="M13 17V5" />
                          <path d="M8 17v-3" />
                        </>
                      )}
                      {item.icon === "coffee" && (
                        <>
                          <path d="M10 2v2" />
                          <path d="M14 2v2" />
                          <path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1" />
                          <path d="M6 2v2" />
                        </>
                      )}
                      {item.icon === "file-text" && (
                        <>
                          <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                          <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                        </>
                      )}
                      {item.icon === "target" && (
                        <>
                          <circle cx="12" cy="12" r="10" />
                          <circle cx="12" cy="12" r="6" />
                          <circle cx="12" cy="12" r="2" />
                        </>
                      )}
                      {item.icon === "brain" && (
                        <>
                          <path d="M12 18V5" />
                          <path d="M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4" />
                        </>
                      )}
                    </svg>
                    <p className="use-case-label">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding-responsive Advanced_Widget_Filtering">
          {/* bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 relative overflow-hidden */}
          {/* Animated Background Blobs */}
          {/* <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" /> */}
          {/* <div
            className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          /> */}
          {/* <div
            className="absolute top-1/2 left-1/2 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          /> */}

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-20-responsive">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div
                  className="inline-flex items-center gap-2 bg-[#093a5b] backdrop-blur-sm border border-white/20 text-white 
                                 rounded-full shadow-lg badge-responsive"
                >
                  {/* <Funnel className="w-5 h-5" /> */}
                  <Zap className="badge-icon-responsive" />
                  <span className="badge-text-responsive">
                    Advanced Widget Filtering
                  </span>
                </div>
                <h2 className="hero-title-dashboard-responsive text-black">
                  Filter Each Widget
                  <br />
                  <span className="hero-title-dashboard-span-responsive text-[#093a5b]">
                    {/* bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent */}
                    Independently
                  </span>
                </h2>
                <p
                  className="hero-desc-responsive
                                 "
                >
                  {/* text-gray-300 max-w-3xl mx-auto leading-relaxed */}
                  Apply custom filters to individual widgets. Each widget can
                  have its own filter settings,
                  <br />
                  giving you precise control over what data you see.
                </p>
              </motion.div>
            </div>

            <div className="max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="grid md:grid-cols-2 grid-gap-responsive mb-12-responsive">
                  {/* Tasks Widget Demo */}
                  <div className="relative">
                    <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 card-padding-responsive">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg icon-box-responsive">
                            <SquareCheckBig className="icon-responsive text-white" />
                          </div>
                          <div>
                            <h3 className="card-title-responsive text-gray-900">
                              My Tasks
                            </h3>
                            <p className="card-subtext-responsive text-gray-500">
                              6 total tasks
                            </p>
                          </div>
                        </div>
                        <button
                          className="p-2 rounded-lg transition-all hover:bg-gray-100 text-gray-600"
                          // onClick={() => setShowFilterPanel((prev) => !prev)}
                          onClick={() => setShowFilterPanel(true)}
                        >
                          <EllipsisVertical className="icon-responsive" />
                        </button>
                      </div>
                      <AnimatePresence>
                        {showFilterPanel && (
                          <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ type: "spring", damping: 25 }}
                            className="absolute top-0 left-[500px] translate-x-[105%] w-80 bg-white rounded-3xl shadow-2xl border border-gray-200 p-6 z-20"
                          >
                            {/* Filter Header */}
                            <div className="flex items-center justify-between mb-6">
                              <div className="flex items-center gap-2">
                                <Filter className="w-5 h-5 text-purple-600" />
                                <h4 className="text-lg text-gray-900">
                                  Filter Options
                                </h4>
                              </div>
                              <button
                                onClick={() => setShowFilterPanel(false)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-all"
                              >
                                <X className="w-4 h-4 text-gray-600" />
                              </button>
                            </div>

                            {/* Filter Options */}
                            <div className="space-y-4 mb-6">
                              {/* Project Filter */}
                              <div>
                                <label className="block text-sm text-gray-700 mb-2 flex items-center gap-2">
                                  <Briefcase className="w-4 h-4" />
                                  Project
                                </label>
                                <div className="relative">
                                  <select
                                    value={activeFilters.project}
                                    onChange={(e) =>
                                      setActiveFilters({
                                        ...activeFilters,
                                        project: e.target.value,
                                      })
                                    }
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 appearance-none cursor-pointer focus:outline-none focus:border-purple-500 transition-all"
                                  >
                                    <option>All Projects</option>
                                    <option>Marketing</option>
                                    <option>Finance</option>
                                    <option>Product</option>
                                    <option>Engineering</option>
                                    <option>Sales</option>
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                                </div>
                              </div>

                              {/* Financial Year Filter */}
                              <div>
                                <label className="block text-sm text-gray-700 mb-2 flex items-center gap-2">
                                  <Calendar className="w-4 h-4" />
                                  Financial Year
                                </label>
                                <div className="relative">
                                  <select
                                    value={activeFilters.financialYear}
                                    onChange={(e) =>
                                      setActiveFilters({
                                        ...activeFilters,
                                        financialYear: e.target.value,
                                      })
                                    }
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 appearance-none cursor-pointer focus:outline-none focus:border-purple-500 transition-all"
                                  >
                                    <option>2024-25</option>
                                    <option>2023-24</option>
                                    <option>2022-23</option>
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                                </div>
                              </div>

                              {/* Period Filter */}
                              <div>
                                <label className="block text-sm text-gray-700 mb-2 flex items-center gap-2">
                                  <Calendar className="w-4 h-4" />
                                  Period
                                </label>
                                <div className="relative">
                                  <select
                                    value={activeFilters.period}
                                    onChange={(e) =>
                                      setActiveFilters({
                                        ...activeFilters,
                                        period: e.target.value,
                                      })
                                    }
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 appearance-none cursor-pointer focus:outline-none focus:border-purple-500 transition-all"
                                  >
                                    <option>This Month</option>
                                    <option>Last Month</option>
                                    <option>This Quarter</option>
                                    <option>Last Quarter</option>
                                    <option>This Year</option>
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                                </div>
                              </div>

                              {/* Assignee Filter */}
                              <div>
                                <label className="block text-sm text-gray-700 mb-2 flex items-center gap-2">
                                  <User className="w-4 h-4" />
                                  Assignee
                                </label>
                                <div className="relative">
                                  <select
                                    value={activeFilters.assignee}
                                    onChange={(e) =>
                                      setActiveFilters({
                                        ...activeFilters,
                                        assignee: e.target.value,
                                      })
                                    }
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 appearance-none cursor-pointer focus:outline-none focus:border-purple-500 transition-all"
                                  >
                                    <option>All Members</option>
                                    <option>John Doe</option>
                                    <option>Jane Smith</option>
                                    <option>Mike Johnson</option>
                                    <option>Sarah Williams</option>
                                    <option>Emily Brown</option>
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                              <button
                                onClick={handleResetFilters}
                                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm transition-all"
                              >
                                Reset
                              </button>
                              <button
                                onClick={handleApplyFilters}
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl text-sm transition-all shadow-lg shadow-purple-500/30 flex items-center justify-center gap-2"
                              >
                                <Check className="w-4 h-4" />
                                Apply Filters
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      <div className="space-y-3 Custom-dashboard-width-class overflow-y-auto pr-2">
                        {[
                          {
                            title: "Website Redesign",
                            category: "Marketing",
                            assignee: "John Doe",
                            status: "In Progress",
                            progress: 65,
                            statusColor: "bg-blue-100 text-blue-700",
                            progressColor: "bg-blue-500",
                          },
                          {
                            title: "Q4 Report",
                            category: "Finance",
                            assignee: "Jane Smith",
                            status: "Completed",
                            progress: 100,
                            statusColor: "bg-green-100 text-green-700",
                            progressColor: "bg-green-500",
                          },
                          {
                            title: "Mobile App Development",
                            category: "Product",
                            assignee: "Mike Johnson",
                            status: "In Progress",
                            progress: 45,
                            statusColor: "bg-blue-100 text-blue-700",
                            progressColor: "bg-purple-500",
                          },
                          {
                            title: "SEO Optimization",
                            category: "Marketing",
                            assignee: "Sarah Williams",
                            status: "Pending",
                            progress: 20,
                            statusColor: "bg-gray-100 text-gray-700",
                            progressColor: "bg-purple-500",
                          },
                          {
                            title: "Database Migration",
                            category: "Engineering",
                            assignee: "John Doe",
                            status: "In Progress",
                            progress: 80,
                            statusColor: "bg-blue-100 text-blue-700",
                            progressColor: "bg-blue-500",
                          },
                          {
                            title: "Client Presentation",
                            category: "Sales",
                            assignee: "Emily Brown",
                            status: "Completed",
                            progress: 100,
                            statusColor: "bg-green-100 text-green-700",
                            progressColor: "bg-green-500",
                          },
                        ].map((task, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            className="bg-gradient-to-br from-gray-50 to-white rounded-xl card-padding-sm-responsive border
                                                         border-gray-200 hover:shadow-md transition-all duration-300"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <p className="text-sm text-gray-900 font-medium">
                                  {task.title}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <Briefcase className="w-3 h-3" />
                                    {task.category}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    •
                                  </span>
                                  <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {task.assignee}
                                  </span>
                                </div>
                              </div>
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${task.statusColor}`}
                              >
                                {task.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  whileInView={{ width: `${task.progress}%` }}
                                  viewport={{ once: true }}
                                  transition={{
                                    duration: 1,
                                    delay: 0.5 + index * 0.1,
                                  }}
                                  className={`h-full rounded-full ${task.progressColor}`}
                                />
                              </div>
                              <span className="text-xs text-gray-500">
                                {task.progress}%
                              </span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Feature Cards */}
                  <div className="space-y-6">
                    <h3 className="heading-text-responsive mb-8-responsive text-black font-medium">
                      Powerful & Flexible
                    </h3>

                    {[
                      {
                        // icon: Funnel,
                        icon: Zap,
                        title: "Widget-Level Filtering",
                        description:
                          "Apply unique filters to each widget independently. One widget can show all projects while another shows only specific ones.",
                        gradient: "from-purple-500 to-pink-500",
                      },
                      {
                        icon: Briefcase,
                        title: "Multiple Filter Types",
                        description:
                          "Filter by Project, Financial Year, Period, and Assignee. Combine multiple filters for precise data views.",
                        gradient: "from-blue-500 to-cyan-500",
                      },
                      {
                        icon: SquareCheckBig,
                        title: "Real-Time Updates",
                        description:
                          "See filtered results instantly. No page reloads, no delays. Just smooth, instant filtering that works.",
                        gradient: "from-green-500 to-emerald-500",
                      },
                    ].map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                        className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl card-padding-6-responsive hover:bg-white/20 transition-all duration-300 group"
                      >
                        <div
                          className={`feature-icon-box-responsive bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                        >
                          <feature.icon className="feature-icon-responsive text-white" />
                        </div>
                        <h4 className="feature-title-responsive text-[#111827] font-semibold mb-2 group-hover:text-purple-300 transition-colors duration-300">
                          {feature.title}
                        </h4>
                        <p className="feature-desc-responsive text-[#4B5563] leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                          {feature.description}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Try It Yourself CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="text-center bg-white/5 backdrop-blur-sm border border-gray-500 rounded-3xl p-12-responsive hover:bg-white/10 transition-all duration-500"
                >
                  <h3 className="try-title-responsive text-[#093a5b] group">
                    Try it yourself{" "}
                    <span className="text-[#093a5b] inline-block group-hover:translate-x-2 transition-transform duration-300">
                      →
                    </span>
                  </h3>
                  <p className="try-desc-responsive text-gray-800 max-w-2xl mx-auto group-hover:text-gray-200 transition-colors duration-300">
                    Click the{" "}
                    <EllipsisVertical className="inline-icon-responsive inline-block mx-1" />{" "}
                    icon on the widget demo above, select your filters, and
                    watch the magic happen in real-time!
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Widget Showcase with Actual Data */}
        <section className="section-padding-responsive bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20-responsive ">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="widgets-title-responsive text-gray-900">
                  20+ Powerful Widgets
                </h2>
                <p className="widgets-desc-responsive text-gray-600 max-w-2xl mx-auto">
                  From analytics to notes, calendars to goals. Everything you
                  need.
                </p>
              </motion.div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 grid-gap-6-responsive">
              {availableWidgets.map((widget, index) => (
                <motion.div
                  key={widget.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05, y: -8 }}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl card-padding-6-responsive border border-gray-200 hover:border-gray-300 hover:shadow-xl transition-all cursor-pointer group"
                >
                  <div
                    className={`widget-icon-box-responsive bg-gradient-to-br ${widget.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-105 group-hover:rotate-3 transition-all shadow-lg`}
                  >
                    <widget.icon className="widget-icon-responsive text-white" />
                  </div>
                  <h4 className="text-lg text-gray-900 mb-2">{widget.name}</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    {widget.description}
                  </p>
                  <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {widget.category}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section - Gradient Background */}
        <section
          className="section-padding-responsive
                  relative overflow-hidden"
        >
          {/* bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 */}
          {/*  */}
          {/* Animated Background Elements */}
          {/* <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" /> */}
          {/* <div
            className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          /> */}

          <div className="max-w-5xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Sparkles className="hero-top-icon text-[#093a5b] mx-auto" />
              {/* text-purple-400 */}
              <h2 className="hero-title-dashboard-responsive text-black">
                Built for Flexibility.
                <br />
                <span className="hero-title-dashboard-span-responsive text-[#093a5b]">
                  {/* bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent */}
                  Designed for You.
                </span>
              </h2>
              <p className="hero-desc-responsive text-[#94A3B8] leading-relaxed">
                No templates to follow. No limits to hit. No learning curve.
                <br />
                Just pure, effortless customization that works the way you
                think.
              </p>

              <div className="grid md:grid-cols-3 hero-grid-responsive">
                {[
                  {
                    icon: Zap,
                    title: "Instant Setup",
                    desc: "Add widgets with one click",
                    gradient: "from-yellow-500 to-orange-500",
                  },
                  {
                    icon: Brain,
                    title: "AI-Powered",
                    desc: "Smart insights automatically",
                    gradient: "from-purple-500 to-pink-500",
                  },
                  {
                    icon: Layout,
                    title: "Auto-Saved",
                    desc: "Never lose your perfect setup",
                    gradient: "from-blue-500 to-cyan-500",
                  },
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className="text-center"
                  >
                    <div
                      className={`feature-box-responsive bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl`}
                    >
                      <feature.icon className="feature-icon-responsive text-white" />
                    </div>
                    <h4 className="feature-title-responsives text-[#093a5B] mb-3">
                      {feature.title}
                    </h4>
                    <p className="text-[#94A3B8] feature-desc-responsive">
                      {feature.desc}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Stats */}
              {/* <div className="flex items-center justify-center gap-16 mt-20 pt-16 border-t border-white/10">
                                <div>
                                    <p className="text-5xl text-white mb-2">10s</p>
                                    <p className="text-gray-400">Setup Time</p>
                                </div>
                                <div className="w-px h-16 bg-white/20" />
                                <div>
                                    <p className="text-5xl text-white mb-2">∞</p>
                                    <p className="text-gray-400">Possibilities</p>
                                </div>
                                <div className="w-px h-16 bg-white/20" />
                                <div>
                                    <p className="text-5xl text-white mb-2">100%</p>
                                    <p className="text-gray-400">Customizable</p>
                                </div>
                            </div> */}
              <div className="stats-wrap-responsive">
                <div className="text-center">
                  <p className="stat-number-responsive text-[#093a5b]">10s</p>
                  <p className="text-gray-400">Setup Time</p>
                </div>

                <div className="stat-divider-responsive" />

                <div className="text-center">
                  <p className="stat-number-responsive text-[#093a5b]">∞</p>
                  <p className="text-gray-400">Possibilities</p>
                </div>

                <div className="stat-divider-responsive " />

                <div className="text-center">
                  <p className="stat-number-responsive text-[#093a5b]">100%</p>
                  <p className="text-gray-400">Customizable</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Full Dashboard Demo Modal */}
        {showFullDashboard && (
          <FullDashboardDemo onClose={() => setShowFullDashboard(false)} />
        )}
      </div>
      <CustomFooter />
    </>
  );
}
